import { Block } from '@/components/block';
import { CountryFlag } from '@/components/country-flag';
import { Heading } from '@/components/heading';
import { Icon } from '@/components/icon';
import { Link } from '@/components/link';
import { Text } from '@/components/text';
import { AIRPORT, MODE_AIR, MODE_WATER, SEAPORT } from '@/lib/constants';
import { FeaturedLinkInterface } from '@/types';
import { NodeType } from '@/types/api-types';
import { BlockId } from '@/types/block-types';
import { tv } from '@/utils/styles';

export interface NearbyPortsBlockProps {
  id: BlockId;
  portName?: string;
  portType?: NodeType;
  portCountry?: string;
  className?: string;
  links?: FeaturedLinkInterface[];
  noTopPadding?: boolean;
}

const NearbyPortsBlock = ({
  id,
  portName = 'Port',
  portType,
  portCountry,
  links,
  noTopPadding = false,
  className,
  ...props
}: NearbyPortsBlockProps) => {
  // Filter into types
  const airports = links?.filter((link) => link.transportType === MODE_AIR);
  const seaports = links?.filter((link) => link.transportType === MODE_WATER);

  // Type titles
  const airportsTitle = portType === AIRPORT ? 'Alternative airports' : 'Nearby airports';
  const seaportsTitle = portType === SEAPORT ? 'Alternative seaports' : 'Nearby seaports';

  const { grid, linksStyles, countryFlag, portLink, text, headingStyles } = styles();

  return (
    <Block id={id} className={className} noTopPadding={noTopPadding} {...props}>
      <div className={grid()}>
        <div>
          <Heading variant="h2" className={headingStyles()}>
            {airportsTitle}
          </Heading>
          <Text>
            {airportsTitle} with regular departures that are near {portName}. Ranked from closest to farthest away.
          </Text>
          <ol className={linksStyles()} data-cy="nearby-ports-links">
            {airports &&
              airports.map((link: FeaturedLinkInterface) => {
                return (
                  <li key={link.key}>
                    <Link className={portLink()} href={link.url}>
                      {portCountry !== link.countryCode && (
                        <CountryFlag countryCode={link.countryCode} className={countryFlag()} />
                      )}
                      <Icon name="plane" color="black" size="5" className="mr-2" />
                      <div>
                        <Heading variant="h5" as="h3" className="m-0">
                          {link.title}
                        </Heading>
                        <Text variant="secondary" className={text()}>
                          {link.secondaryText}
                        </Text>
                      </div>
                    </Link>
                  </li>
                );
              })}
          </ol>
        </div>
        <div>
          <Heading variant="h2" className={headingStyles()}>
            {seaportsTitle}
          </Heading>
          <Text>
            {seaportsTitle} with regular departures that are near {portName}. Ranked from closest to farthest away.
          </Text>
          <ol className={linksStyles()} data-cy="nearby-ports-links">
            {seaports &&
              seaports.map((link: FeaturedLinkInterface) => {
                return (
                  <li key={link.key}>
                    <Link className={portLink()} href={link.url}>
                      {portCountry !== link.countryCode && (
                        <CountryFlag countryCode={link.countryCode} className={countryFlag()} />
                      )}
                      <Icon name="ship" color="black" size="5" className="mr-2" />
                      <div>
                        <Heading variant="h5" as="h3" className="m-0">
                          {link.title}
                        </Heading>
                        <Text variant="secondary" className={text()}>
                          {link.secondaryText}
                        </Text>
                      </div>
                    </Link>
                  </li>
                );
              })}
          </ol>
        </div>
      </div>
    </Block>
  );
};

const styles = tv({
  slots: {
    grid: 'grid grid-cols-1 gap-y-8 md:grid-cols-2 md:gap-x-8',
    linksStyles: 'mt-8 grid gap-y-2 md:grid-cols-2 md:gap-x-4 md:gap-y-4',
    countryFlag: 'absolute -right-1 -top-1 h-4 rounded-sm',
    portLink:
      'relative grid h-full cursor-pointer grid-cols-[1.5rem_1fr] rounded-lg border border-grey-300 bg-white p-4 text-text-primary no-underline hover:border-grey-300 hover:shadow-soft',
    text: 'm-0 mt-0.5 flex items-center',
    headingStyles: 'mb-2 text-blue-800',
  },
});

export { NearbyPortsBlock };
